import { Box, Typography } from '@mui/material';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { statusLogsSelector, useGetTransactionDetailsQuery } from '@/api/v1';
import SettlementTimeIcon from '@/assets/icons/settlement-time-icon.svg?react';
import { Alert } from '@/components/molecules/Alert';
import { ExpectedSettlementTimeInfo } from '@/components/molecules/ExpectedSettlementTimeInfo';
import { PixQRCodeSection } from '@/components/molecules/PixQRCodeSection';
import { SpeiClabeSection } from '@/components/molecules/SpeiClabeSection';
import { TransactionDetailsDrawerStatusItem } from '@/components/molecules/TransactionDetailsDrawerStatusItem';
import { TransactionDetailsDrawerStatusPlaceholderItem } from '@/components/molecules/TransactionDetailsDrawerStatusPlaceholderItem';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useTranslator } from '@/hooks/useTranslator';
import { StatusType } from '@/types/transaction';
import { isPixInLocalInfo, isSpeiInLocalInfo } from '@/types/transaction.ext';
import { TransactionStatusLog } from '@/types/transactionStatusLog';
import { StatusPlaceholder } from '@/types/transactionStatusLogs';

interface StatusDisplayInfo {
  readonly label: string;
  readonly status: 'completed' | 'in-progress' | 'pending';
  readonly description: string;
  readonly date: string;
}

interface Props {
  readonly statusLogs?: readonly TransactionStatusLog[];
  readonly nextStatus?: readonly string[];
}

export const TransactionDetailsDrawerStatus: React.FC<Props> = (
  props: Props,
): React.ReactElement => {
  const { transactionId = '' } = useParams<{ readonly transactionId: string | undefined }>();
  const { isLoading, error } = useSelector(statusLogsSelector(transactionId));
  const { data: transaction } = useGetTransactionDetailsQuery(transactionId);
  const expectedSettlementTimeDetails =
    transaction?.transactionDetails.expectedSettlementTimeDetails;
  const { statusLogs = [], nextStatus = [] } = props;
  const tr = useTranslator();

  const pixQRCodeInfo = React.useMemo((): {
    pixCopyAndPaste: string;
    expiresAt: string;
  } | null => {
    if (!transaction) {
      return null;
    }

    const { status, localInfo, expiresAt } = transaction;

    if (status !== StatusType.CREATED) {
      return null;
    }

    if (!isPixInLocalInfo(localInfo)) {
      return null;
    }

    return {
      pixCopyAndPaste: localInfo.pixCopyAndPaste,
      expiresAt: expiresAt ?? localInfo.pixExpiresAt,
    };
  }, [transaction]);

  const speiClabeInfo = React.useMemo((): {
    clabe: string;
    bankName: string;
    beneficiaryName: string;
    expiresAt: string;
  } | null => {
    if (!transaction) {
      return null;
    }

    const { status, localInfo, expiresAt } = transaction;

    if (status !== StatusType.CREATED) {
      return null;
    }

    if (!isSpeiInLocalInfo(localInfo) || !localInfo.speiInClabe) {
      return null;
    }

    if (!expiresAt) {
      return null;
    }

    return {
      clabe: localInfo.speiInClabe,
      bankName: localInfo.speiInBankName,
      beneficiaryName: localInfo.speiInBeneficiaryName,
      expiresAt,
    };
  }, [transaction]);

  const hasFundingInfo = pixQRCodeInfo !== null || speiClabeInfo !== null;

  // Reverse the array to show chronological order (oldest first)
  const sortedStatusLogs = React.useMemo(
    (): readonly TransactionStatusLog[] =>
      [...statusLogs]
        .filter((statusLog: TransactionStatusLog): boolean => !statusLog.internal)
        .reverse(),
    [statusLogs],
  );

  // Create next status items if they exist
  const nextStatusItems = React.useMemo(
    (): readonly StatusPlaceholder[] =>
      nextStatus.map(
        (status: string): StatusPlaceholder => ({
          label: tr(status),
          status: 'pending' as const,
          description: tr(`${status}_DESC`),
          date: null,
        }),
      ),
    [nextStatus, tr],
  );

  const dateFormatter = useDateFormatter('MMM d, yyyy | hh:mm a');
  const mapStatusToDisplayInfo = React.useCallback(
    (
      statusLog: TransactionStatusLog,
      index: number,
      transactionStatusLogs: readonly TransactionStatusLog[],
    ): StatusDisplayInfo => {
      const { status } = statusLog;

      const description =
        status === 'CREATED' && hasFundingInfo
          ? tr('CREATED_WAITING_FUNDING_DESC')
          : `${status}_DESC`;

      const mappedInfo = {
        label: status,
        description,
        date: dateFormatter.parseAndFormat(statusLog.createdAt),
      };

      if (index === transactionStatusLogs.length - 1 && !statusLog.final) {
        return {
          ...mappedInfo,
          status: 'in-progress',
        };
      }

      return {
        ...mappedInfo,
        status: 'completed',
      };
    },
    [dateFormatter, hasFundingInfo, tr],
  );

  const renderLogItem = React.useCallback(
    (statusLog: TransactionStatusLog, index: number): React.ReactElement => {
      const displayInfo = mapStatusToDisplayInfo(statusLog, index, sortedStatusLogs);

      return (
        <Box key={`box-${statusLog.status}-${index}`} position="relative">
          {(nextStatusItems.length > 0 || index + 1 < sortedStatusLogs.length) && (
            <Box sx={styles.divider} />
          )}
          <TransactionDetailsDrawerStatusItem
            key={`${statusLog.status}-${index}`}
            label={displayInfo.label}
            status={displayInfo.status}
            date={displayInfo.date}
            description={displayInfo.description}
            defaultExpanded={statusLog.status === 'CREATED' && hasFundingInfo}>
            {statusLog.status === 'CREATED' && pixQRCodeInfo !== null && (
              <Box mt={2}>
                <PixQRCodeSection
                  pixCopyAndPaste={pixQRCodeInfo.pixCopyAndPaste}
                  expiresAt={pixQRCodeInfo.expiresAt}
                  transactionId={transactionId}
                />
              </Box>
            )}
            {statusLog.status === 'CREATED' && speiClabeInfo !== null && (
              <Box mt={2}>
                <SpeiClabeSection
                  clabe={speiClabeInfo.clabe}
                  bankName={speiClabeInfo.bankName}
                  beneficiaryName={speiClabeInfo.beneficiaryName}
                  expiresAt={speiClabeInfo.expiresAt}
                  transactionId={transactionId}
                />
              </Box>
            )}
          </TransactionDetailsDrawerStatusItem>
        </Box>
      );
    },
    [
      mapStatusToDisplayInfo,
      nextStatusItems.length,
      sortedStatusLogs,
      pixQRCodeInfo,
      speiClabeInfo,
      hasFundingInfo,
      transactionId,
    ],
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <Typography variant="body2" color="textSecondary">
          {tr('LOADING_TRANSACTION_STATUS')}
        </Typography>
      </Box>
    );
  }

  if (error || !statusLogs || statusLogs.length === 0) {
    return <></>;
  }

  return (
    <Box display="flex" flexDirection="column" gap={2} pb={2}>
      <Box display="flex" flexDirection="column" position="relative" py={2} gap={4}>
        {sortedStatusLogs.map(renderLogItem)}
        {nextStatusItems.map(
          (nextStatusItem: StatusPlaceholder, index: number): React.ReactElement => (
            <Box key={`box-${nextStatusItem.status}-${index}`} position="relative">
              {index + 1 < nextStatusItems.length && <Box sx={styles.divider} />}
              <TransactionDetailsDrawerStatusPlaceholderItem
                key={`next-${nextStatusItem.label}-${index}`}
                label={tr(nextStatusItem.label)}
                status={nextStatusItem.status}
              />
            </Box>
          ),
        )}
      </Box>
      {expectedSettlementTimeDetails && (
        <Alert
          sx={styles.alert}
          variant="info"
          leftIcon={<SettlementTimeIcon stroke="currentColor" />}
          // removing the icon until the product team makes up the copy for the info button
          // rightIcon={<InfoIcon fill={theme.palette.custom.LightGray} />}
        >
          <ExpectedSettlementTimeInfo
            expectedSettlementTimeDetails={expectedSettlementTimeDetails}
            settlementTimeTextStyles={styles.settlementTimeText}
          />
        </Alert>
      )}
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  divider: {
    width: '1px',
    backgroundColor: 'custom.Inactive',
    position: 'absolute',
    top: '27px',
    bottom: '-28px',
    left: '7px',
  },
  alert: {
    py: 2,
  },
  settlementTimeText: {
    color: 'primary.main',
  },
};
